<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import axios from 'axios';

import {
  kBlock,
  kBlockTitle,
  kList,
  kListItem,
  kButton,
  kRadio,
} from 'konsta/vue'

const env = import.meta.env;

import { useConfig } from '@/stores/config'
const configStore = useConfig()
const config = configStore.config

import {useLoader} from '@/stores/loader'
const LoaderStore = useLoader();

import {payments} from '@bitroadie/bit-essential'

const stripe = payments.useStripe()
const stripe_card_show = ref(false)

const paypal_advanced = payments.usePaypalAdvanced()

import { useBooking } from '@/stores/booking'
const bookingStore = useBooking()

import { BuildingLibraryIcon, CreditCardIcon } from '@heroicons/vue/24/outline';

const emit = defineEmits(['success']);

const selected_method = ref('')

const appointment = ref(bookingStore.appointment)

function toggleDetailsStripeCreditCard() {
  stripe_card_show.value = true;

  stripe.endpoint_get_payment_intent_data = {
    token : appointment.value.token
  }

  stripe.error = null;
  stripe.init(config?.stripe?.public_key);
}

function stripePay() {
  selected_method.value = 'stripe'
  LoaderStore.open();
  stripe.pay().then((response) => {
    emit('success', response)
  }).catch((error) => {
    console.error(error)
    // notify.add('Errore', 'Si è verificato un errore durante il pagamento gratuito.', 'error');
    // emit('payment-error', error);
  }).finally(() => {
    LoaderStore.close();
  });
}

const toggleDetailsBankwire = ref(false)

async function bankwire_pay(){
  if(bookingStore.appointment) {
    LoaderStore.open();
    try{
      const response = await axios.post("/payment/bankwire/confirm", {
        token : bookingStore.appointment.token
      }); 

      if(response.data.status == 'success'){
        emit('success', response.data)
      }
    }catch(e){
      console.error(e)
      if(e.response && e.response.status == 400 && e.response.data && e.response.data.message){
        errors.value = e.response.data.message
      } else throw e
    }finally{
      LoaderStore.close();
    }
  }
}

function toggleDetailsBankWire(){
  selected_method.value = 'bank_wire'
  toggleDetailsBankwire.value = !toggleDetailsBankwire.value
}


onMounted(() => {
  loadPaypal()
})

function loadPaypal(){

  const methods = configStore.config?.payment_methods_enabled

  const paypal_client_id = configStore.config?.paypal_advanced?.client_id || env.VITE_PAYPAL_CLIENT_ID
  const paypal_merchant_id = configStore.config?.paypal_advanced?.merchant_id || env.VITE_PAYPAL_MERCHANT_ID

  if(methods.includes('paypal_advanced') || methods.includes('paypal_advanced_paylater') || methods.includes('paypal_advanced_card')){
    const script = document.createElement("script");
    script.src = 'https://www.paypal.com/sdk/js?components=applepay,googlepay,messages,buttons,card-fields' + (methods.includes('paypal_advanced_paylater') && bookingStore.appointment.price_with_tax >= 30 ? '&enable-funding=paylater' : '') + '&disable-funding=mybank' + (!methods.includes('paypal_advanced_card') ? ',card' : '') +'&currency=EUR&client-id=' +paypal_client_id+'&locale=it_IT&merchant-id=' + paypal_merchant_id;
    document.body.appendChild(script);

    script.onload = () => {
      window.PaypalAdvancedLoaded = true;

      // if(config.payment_methods_enabled.includes('paypal_advanced_applepay') && window.PaypalAdvancedApplepayLoaded){
      //   const PaypalAdvancedApplepayInit = new Event("PaypalAdvancedApplepayInit"); 
      //   document.dispatchEvent(PaypalAdvancedApplepayInit);
      // }

      // if(config.payment_methods_enabled.includes('paypal_advanced_googlepay') && window.PaypalAdvancedGooglepayLoaded){
      //   const PaypalAdvancedGooglepayInit = new Event("PaypalAdvancedGooglepayInit"); 
      //   document.dispatchEvent(PaypalAdvancedGooglepayInit);
      // }

      if(methods.includes('paypal_advanced') || (methods.includes('paypal_advanced_paylater') && bookingStore.appointment.price_with_tax >= 30)){
        // paypal advanced: paypal + paylater
        paypal_advanced.success_callback = (response, send_data) => {
          emit('success')
        };

        paypal_advanced.endpoint_create_order_data = {
          token : appointment.value.token
        }
        paypal_advanced.endpoint_confirm_data = {
          token : appointment.value.token
        }

        paypal_advanced.axios = axios;
        paypal_advanced.methods = methods;
        paypal_advanced.init();
      }
    }
  }
}



const optional_services_total = computed(() => {
  if (!bookingStore.appointment?.optional_services) return 0
  return bookingStore.appointment.optional_services.reduce((sum, os) => {
    const price = os.billing_type === 'per_day'
      ? os.price_with_tax * (bookingStore.bnb_nights || 1)
      : os.price_with_tax
    return sum + price
  }, 0)
})

const base_price = computed(() => {
  if (!bookingStore.price_with_tax) return 0
  return bookingStore.price_with_tax - optional_services_total.value
})

function resetMethod() {
  selected_method.value = ''
  toggleDetailsBankwire.value = false
  stripe_card_show.value = false
}
</script>

<template>
  <div>
    <k-block-title class="!mb-4">Metodi di pagamento</k-block-title>

    <k-block v-if="selected_method !== ''">
      <button
        type="button"
        class="text-sm text-gray-500 flex items-center gap-1"
        @click="resetMethod">
        ← Cambia metodo di pagamento
      </button>
    </k-block>

    <!-- Bonifico: visibile se nessun metodo selezionato O se è quello selezionato -->
    <k-block v-if="config.payment_methods_enabled.includes('bank_wire') && (selected_method === '' || selected_method === 'bank_wire')"
      class="!my-0 !mt-8 !mb-4">
      <div class="w-full rounded-md flex flex-col items-center border bg-white"
        :class="selected_method === 'bank_wire' ? 'p-4' : ''">
        
        <div v-if="toggleDetailsBankwire" class="mt-4 w-full">
          <h2 class="text-lg font-semibold text-gray-800">Bonifico Bancario</h2>
          <p class="mt-2 text-gray-600">Effettua un bonifico utilizzando le seguenti informazioni:</p>
          <ul class="mt-4 space-y-2 text-gray-700">
            <li><strong>Beneficiario:</strong> {{ configStore.account.beneficiary }}</li>
            <li><strong>IBAN:</strong> {{ configStore.account.iban }}</li>
            <li><strong>BIC/SWIFT:</strong> {{ configStore.account?.bic_swift }}</li>
            <li><strong>Nome banca:</strong> {{ configStore.account?.bank_name }}</li>
            <li><strong>Indirizzo banca:</strong> {{ configStore.account?.bank_address }}</li>
            <li><strong>Causale:</strong> Inserire il numero d'ordine <span class="font-medium text-gray-800">#12345</span></li>
          </ul>
          <p class="mt-4 text-sm text-gray-600">{{ configStore.account?.note }}</p>
        </div>
        <button 
          v-if="!toggleDetailsBankwire"
          type="button"
          class="w-full bg-white rounded-md p-4 flex justify-center items-center"
          @click="toggleDetailsBankWire()"
        >
          <BuildingLibraryIcon class="w-6 mr-2"/> Bonifico Bancario
        </button>
        <button
          v-else
          type="button"
          class="w-full text-white rounded-md bg-primary p-4"
          @click="bankwire_pay"
        >
          Concludi ordine
        </button>
      </div>
    </k-block>
    <k-block v-if="selected_method === '' || selected_method === 'stripe'"
      class="!my-4">
      <div 
        :class="[
          // !terms_conditions ? 'opacity-50 pointer-events-none' : ''
        ]" 
        id="card" 
        class="card_container mt-4">
        <!-- v-if="config.payment_methods_enabled.includes('stripe')"  -->
        <div v-show="stripe_card_show" class="mt-4 bg-white p-4 rounded-md border">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Numero carta</label>
              <div id="card-number-element" class="p-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500"></div>
            </div>
            
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Scadenza</label>
                <div id="card-expiry-element" class="p-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500"></div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                <div id="card-cvc-element" class="p-3 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-blue-500"></div>
              </div>
            </div>
          </div>
          
          <button 
            v-show="stripe_card_show"
            @click="stripePay()"
            class="w-full mt-4 rounded-md flex justify-center items-center p-4 bg-blue-900 text-white"
            type="button">
            <CreditCardIcon class="w-6 mr-2" />Paga con Carta di Credito
          </button>
        </div>
        
        <div v-if="stripe.error" class="text-red-500 mt-2">{{ stripe.error }}</div>
        
        <button 
          v-show="!stripe_card_show"
          @click="toggleDetailsStripeCreditCard()"
          type="button" 
          class="w-full rounded-md flex justify-center items-center p-4 bg-blue-900 border text-white">
          <CreditCardIcon class="w-6 mr-2" />Paga con Carta di Credito
        </button>

      </div>
    </k-block>
    <k-block class="!my-4">
      <div 
        v-show="
          selected_method === '' && (
          config.payment_methods_enabled.includes('paypal_advanced') ||
          config.payment_methods_enabled.includes('paypal_advanced_paylater')
        )"
        id="paypal-button-container" 
        class=""/>

    </k-block>


  </div>
</template>